import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Grid, Form, Input, Button, Message, Upload, Typography, Select, Card, Divider, Spin } from "@arco-design/web-react";
import { getAuth } from "firebase/auth";
import axios from "axios";
import classNames from 'classnames';

import { endpoints } from "../../api/endpoints";
import useFetchSearchFilters from "../../api/hooks/useFetchSearchFilters";
import { AvatarCropDialog } from '../../components/AvatarCropDialog/AvatarCropDialog';
import { UserAvatar } from '../../components/UserAvatar/UserAvatar';
import { SMSSettings } from '../../components/SMSSettings/SMSSettings';
import { LanguagePills } from '../../components/LanguagePills/LanguagePills';

import { AVATAR_IMAGE_TYPES } from '../../constants/imageTypes';
import { routes } from "../../routes/routes";
import { cachedToken } from "../../setupAxios";
import { setNewAvatar, setUser } from "../../store/userSlice";
import { RootState } from "../../store/store";
import { fixUrl } from "../../utils/fixUrl";
import { getButtonColor, getButtonDisabledColor, getButtonTextColor } from "../../utils/cssVariables";
import { checkEmptyField, validateEmailField, validateUrlField, validateZipCode } from "../../utils/validators/userFormValidators";
import { useTranslations } from "../../utils/hooks/useTranslations";
import { ownProfilePathAfterUpdate } from "../../utils/ownProfilePath";

import './Profile.css';

const Row = Grid.Row;
const Col = Grid.Col;
const TextArea = Input.TextArea;
const FormItem = Form.Item;

export const ProfileSettings = () => {
  const { t } = useTranslation();

  const user = useSelector((state: RootState) => state.user.user);
  const currentUser = getAuth().currentUser;

  const isCalendlyLinkRequired = useSelector((state: RootState) => state.config.isCalendlyLinkRequired);
  const isAutoGoogleMeetEnabled = useSelector((state: RootState) => state.config.isAutoGoogleMeetEnabled);
  const isSurveyForm = useSelector((state: RootState) => state.config.isSurveyForm);
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);
  const isMenteeIntakeFieldRequired = isSurveyForm && useNycIntake;
  const calendlyFieldRequired = isCalendlyLinkRequired && !useNycIntake;

  const [form] = Form.useForm();
  const mwbeValue = Form.useWatch('mwbe', form);
  const showMwbeOther = useNycIntake && mwbeValue === 'Other';

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [privateProfile, setPrivateProfile] = useState<any>(null);

  const { isLoading } = useFetchSearchFilters();
  const { formLists, translateSelected } = useTranslations();

  const professionalExperienceOptions = useMemo(
    () => useNycIntake
      ? formLists.professionalExperienceOptions.filter((o) => o.value !== 'Other (please specify)')
      : formLists.professionalExperienceOptions,
    [useNycIntake, formLists.professionalExperienceOptions],
  );

  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedAdvisorCategories, setSelectedAdvisorCategories] = useState<string[]>([]);
  const [firstGoal, setFirstGoal] = useState<string>('');
  const [secondGoal, setSecondGoal] = useState<string>('');
  const [thirdGoal, setThirdGoal] = useState<string>('');

  const isBusinessFieldsAvailableInitial = privateProfile && (privateProfile.occupationStatus === 'Entrepreneur (Business Owner/Self-Employed)');

  const [isBusinessFieldsAvailable, setIsBusinessFieldsAvailable] = useState(isBusinessFieldsAvailableInitial);

  const handleChangeOccupationStatus = (value: string) => {
    setIsBusinessFieldsAvailable(value === 'Entrepreneur (Business Owner/Self-Employed)')
  }

  const [isAvatarUprating, setIsAvatarUpdating] = useState<boolean>(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isFileTypeError, setIsFileTypeError] = useState(false);
  const resolverRef = useRef<(value: File) => void>();
  const rejecterRef  = useRef<() => void>();

  const beforeUpload = (file: File) => {
    if (!AVATAR_IMAGE_TYPES.includes(file.type)) {
      Message.warning(t('form_validation_messages.invalid_file_type'));
      setIsFileTypeError(true);
      return false;
    }

    setIsFileTypeError(false);
    return new Promise<File | boolean>((resolve, reject) => {
      resolverRef.current = resolve;
      rejecterRef.current = reject;
      setImageFile(file)
      setShowCropDialog(true);
    });
  }

  const clearRefs = () => {
    resolverRef.current = undefined;
    rejecterRef.current = undefined;
  };

  const handleChangeIndustries = (value: string[]) => {
    if (value.length > 3) {
      Message.warning(t('notification_messages.industries_length_exceeded'));
      return;
    }
    setSelectedIndustries(value);
    form.setFieldsValue({ current_industry: value });
  };

  const handleChangeGoals = (value: string[]) => {
    if (value.length > 3) {
      Message.warning(t('notification_messages.goals_length_exceeded'));
      return;
    }
    setSelectedGoals(value || []);
  };


  const getGoalsOptions = (options: {
    label: string;
    value: string;
  }[]) => {
    return options.map((option) => ({
      ...option,
      disabled:
        selectedGoals.length >= 3 && !selectedGoals.includes(option.value),
    }));
  };

  const handleChangeAdvisorCategories = (value: string[]) => {
    if (value.length > 3) {
      Message.warning(t('notification_messages.advisor_categories_length_exceeded'));
      return;
    }
    setSelectedAdvisorCategories(value || []);
    form.setFieldsValue({ categories: value || [] });
  };

  const getAdvisorCategoriesOptions = (options: {
    label: string;
    value: string;
  }[]) => {
    return options.map((option) => ({
      ...option,
      disabled:
        selectedAdvisorCategories.length >= 3 && !selectedAdvisorCategories.includes(option.value),
    }));
  };

  const getFirstGoalOptions = () => {
    return formLists.three_goals.filter(opt => opt.value !== secondGoal && opt.value !== thirdGoal);
  };

  const getSecondGoalOptions = () => {
    return formLists.three_goals.filter(opt => opt.value !== firstGoal && opt.value !== thirdGoal);
  };

  const getThirdGoalOptions = () => {
    return formLists.three_goals.filter(opt => opt.value !== firstGoal && opt.value !== secondGoal);
  };


  const [idToken, setIdToken] = useState<string | null>(null);

  const getIndustriesOptions = (options: {
    label: string;
    value: string;
  }[]) => {
    return options.map((option) => ({
      ...option,
      disabled:
        selectedIndustries.length >= 3 && !selectedIndustries.includes(option.value),
    }));
  };

  useEffect(() => {
    const loadToken = async () => {
      try {
        const token = cachedToken ?? await currentUser?.getIdToken();

        if (token) {
          setIdToken(token);
        } else {
          console.warn("No token available for avatar upload");
        }
      } catch (error) {
        console.error("Failed to load token:", error);
      }
    };

    loadToken();
  }, [currentUser]);

  const prepareProfileData = (profileData: any) => {
    const dataToSend = { ...profileData };

    // Convert current_industry array to ;;;-separated string
    if (Array.isArray(dataToSend.current_industry)) {
      dataToSend.current_industry = dataToSend.current_industry.join(';;;');
    }

    const fieldsToClean = [
      'borough', 'gender', 'age', 'race', 'ethnicity',
      'education', 'incomeRange', 'occupationStatus', 
      'professionalExperience', 'howYouFoundUs', 'business_start_period', 'mwbe', 'employees_count', 'business_stage', 'important_goal', 'business_location', 'revenue', 'item_types',
      'first_goal', 'second_goal', 'third_goal', 'long_term_goal', 'advisor_title', 'advisor_company', 'sub_industry', 'company_name', 'zip_code',
    ];

    fieldsToClean.forEach(field => {
      if (dataToSend[field] === undefined || dataToSend[field] === null) {
        dataToSend[field] = "";
      }
    });

    if (useNycIntake) {
      if (typeof dataToSend.highGrowthIndustries === 'string' && dataToSend.highGrowthIndustries) {
        dataToSend.highGrowthIndustries = [dataToSend.highGrowthIndustries];
      }
      if (dataToSend.mwbe === 'Other') {
        const otherText = dataToSend.mwbe_other?.trim();
        if (otherText) {
          dataToSend.mwbe = `Other: ${otherText}`;
        }
      }
      delete dataToSend.mwbe_other;
    }

    return dataToSend;
  };

  const onSubmit = async () => {
    if (form) {
      const fields = form.getFieldsValue();
      const preparedData = prepareProfileData(fields);

      form.validate().then(() => {
        axios
          .post(endpoints.updateProfile, {...preparedData, avatar_url: privateProfile.avatar_url})
          .then((response) => {
            if (user) {
              navigate(ownProfilePathAfterUpdate(user, privateProfile.id));
            }
            dispatch(setUser(response.data));
            Message.success({ content: t('notification_messages.profile_updated_success'), closable: true, duration: 3000});
          })
          .catch((error) => {
            console.error(error);
            Message.error(t('notification_messages.profile_updated_error'));
          })
      });
    }
  };

  const cleanEmptyValues = useCallback((userData: any) => {
    return {
      ...userData,
      borough: userData.borough || undefined,
      gender: userData.gender || undefined,
      age: userData.age || undefined,
      race: userData.race || undefined,
      ethnicity: userData.ethnicity || undefined,
      education: userData.education || undefined,
      incomeRange: userData.incomeRange || undefined,
      occupationStatus: userData.occupationStatus || undefined,
      professionalExperience: userData.professionalExperience || undefined,
      howYouFoundUs: userData.howYouFoundUs || undefined,      
      business_start_period: userData.business_start_period || undefined,
      mwbe: userData.mwbe || undefined,
      employees_count: userData.employees_count || undefined,
      business_stage: userData.business_stage || undefined,
      important_goal: userData.important_goal || undefined,
      business_location: userData.business_location || undefined,
      revenue: userData.revenue || undefined,
      item_types: userData.item_types || undefined,
      areaInterest: userData.areaInterest || undefined,
      first_goal: userData.first_goal || undefined,
      second_goal: userData.second_goal || undefined,
      third_goal: userData.third_goal || undefined,
      long_term_goal: userData.long_term_goal || undefined,
      advisor_title: userData.advisor_title || undefined,
      advisor_company: userData.advisor_company || undefined,
      current_industry: userData.current_industry 
        ? (Array.isArray(userData.current_industry) 
          ? userData.current_industry 
          : (typeof userData.current_industry === 'string' && userData.current_industry.includes(';;;')
            ? userData.current_industry.split(';;;').filter((s: string) => s.trim())
            : [userData.current_industry]))
        : undefined,
      sub_industry: userData.sub_industry || undefined,
      company_name: userData.company_name || undefined,
      zip_code: userData.zip_code || undefined
    };
  }, []); 

  useEffect(() => {
    axios
      .get(endpoints.currentProfile)
      .then((response) => {
        setPrivateProfile(response.data);
        const cleaned = cleanEmptyValues(response.data);
        form.setFieldsValue(cleaned);
        setIsBusinessFieldsAvailable(response.data.occupationStatus === 'Entrepreneur (Business Owner/Self-Employed)')
        setSelectedIndustries(cleaned?.current_industry || [])
        setSelectedAdvisorCategories(response.data?.categories || [])
        if (useNycIntake && response.data?.isMentor) {
          const industry = cleaned?.current_industry;
          const singleIndustry = Array.isArray(industry) ? industry[0] : industry;
          form.setFieldsValue({ current_industry: singleIndustry || undefined });
        }
        if (useNycIntake && !response.data?.isMentor) {
          const highGrowth = response.data?.highGrowthIndustries;
          const singleHighGrowth = Array.isArray(highGrowth) ? highGrowth[0] : highGrowth;
          if (singleHighGrowth) {
            form.setFieldsValue({ highGrowthIndustries: singleHighGrowth });
          }
          const industry = response.data?.current_industry;
          const singleIndustry = Array.isArray(industry)
            ? industry[0]
            : (typeof industry === 'string' && industry.includes(';;;')
              ? industry.split(';;;').filter((s: string) => s.trim())[0]
              : industry);
          if (singleIndustry) {
            form.setFieldsValue({ current_industry: singleIndustry });
          }
        }
        if (useNycIntake && response.data?.mwbe?.startsWith('Other:')) {
          form.setFieldsValue({
            mwbe: 'Other',
            mwbe_other: response.data.mwbe.slice(6).trim(),
          });
        }
        setFirstGoal(response.data?.first_goal || '')
        setSecondGoal(response.data?.second_goal || '')
        setThirdGoal(response.data?.third_goal || '')
      })
      .catch((error) => {
        console.error(error);
      })
  }, [useNycIntake]);

  if (isLoading) {
    return <div style={{
      width: "100%",
      height: "40%"
    }}
    > <Spin style={{ margin: '120px auto' }} size={32} />
    </div>
  }

  return (
    <Card
      style={{
        width: "100%",
      }}
      className="card-with-icon-hover own-card profile-settings-card"
    >
      <Row className="profile-settings-card__inner-row" style={{ margin: 10, marginTop: 0 }} justify="center">
        <Col span={24} xl={{ span: 24 }} xs={{ span: 24 }} md={{ span: 18 }}>
          <Typography.Title
            heading={3}
            style={{
              textAlign: "left",
              margin: "10px 0 20px 0",
              wordBreak: "break-word",
              textTransform: "uppercase",
            }}
          >
            {t('profile.edit_profile_heading')}
          </Typography.Title>

          {!!privateProfile && !!privateProfile?.email && (
            <>
              <Form
                form={form}
                autoComplete="off"
                size={"default"}
                scrollToFirstError
                className="profile-user-form"
                onSubmit={onSubmit}
              >
                <Form.Item
                  layout="vertical"
                  label={t('profile.language_label')}
                  field="language"
                >
                  <LanguagePills />
                </Form.Item>
                <FormItem layout="vertical" label={t('profile.name_label')} field="name" rules={[{ required: true, message: t('form_validation_messages.empty_name'), validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_name'))}]}>
                  <Input
                    placeholder={t('profile.name_placeholder')} 
                    autoComplete={"off"}
                  />
                </FormItem>
                <Form.Item
                  layout="vertical"
                  label={t('profile.last_name_label')}
                  field="last_name"
                  rules={[{
                    required: true,
                    message: t('form_validation_messages.empty_last_name'),
                    validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_last_name'))
                  }]}
                >
                  <Input
                    placeholder={t('profile.last_name_placeholder')}
                    autoComplete="off"
                  />
                </Form.Item>
                {!privateProfile?.isMentor && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.company_name_label')}
                    field="company_name"
                  >
                    <Input
                      placeholder={t('profile.company_name_placeholder')}
                      autoComplete="off"
                    />
                  </Form.Item>
                )}
                <Form.Item
                  layout="vertical"
                  label={t('profile.email_label')}
                  field="email"
                  disabled
                  rules={[
                    { required: true, 
                      message: t('form_validation_messages.empty_email')
                    }, 
                    {validator: (v, cb) => validateEmailField(v, cb, t)}
                  ]}
                >
                  <Input
                    placeholder={t('profile.email_placeholder')}
                    autoComplete="off"
                  />
                </Form.Item>
                <Form.Item
                  layout="vertical"
                  label={t('profile.linkedin_label')}
                  field="linkedIn"
                  rules={[
                    { required: false },
                    {validator: (value, callback) => validateUrlField(value, callback,t, false, 'LinkedIn', 'linkedin.com')}
                  ]}
                >
                  <Input
                    placeholder={t('profile.linkedin_placeholder')}
                    autoComplete="off"
                    onBlur={() => {
                      form.setFieldsValue({
                        linkedIn: fixUrl(form.getFieldValue("linkedIn")),
                      });
                    }}
                  />
                </Form.Item>

                {!privateProfile?.isMentor && (
                  <>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.zip_code_label')}
                      field="zip_code"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        validator: (v, cb) => validateZipCode(v, cb, t, isMenteeIntakeFieldRequired)
                      }]}
                    >
                      <Input 
                        type="text" 
                        placeholder={t('profile.zip_code_placeholder')}
                        maxLength={5}
                      />
                    </Form.Item>
                    {!useNycIntake && (
                    <Form.Item
                      layout="vertical"
                      label={t('profile.borough_label')}
                      field="borough"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_borough'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_borough')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.borough_placeholder')}
                        options={formLists.boroughOptions}
                        allowClear
                      />
                    </Form.Item>
                    )}
                    <Form.Item
                      layout="vertical"
                      label={t('profile.gender_label')}
                      field="gender"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_gender'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_gender')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.gender_placeholder')}
                        options={formLists.genderOptions}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.age_label')}
                      field="age"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_age'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_age')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.age_placeholder')}
                        options={formLists.ageOptions}
                        allowClear                  
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.race_label')}
                      field="race"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_race'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_race')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.race_placeholder')}
                        options={formLists.raceOptions}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.ethnicity_label')}
                      field="ethnicity"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_ethnicity'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_ethnicity')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.ethnicity_placeholder')}
                        options={formLists.ethnicityOptions}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.education_label')}
                      field="education"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_education'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_education')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.education_placeholder')}
                        options={formLists.educationOptions}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.income_range_label')}
                      field="incomeRange"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_income_range'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_income_range')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.income_range_placeholder')}
                        options={formLists.incomeRangeOptions}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.occupation_status_label')}
                      field="occupationStatus"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_occupation_status'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_occupation_status')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.occupation_status_placeholder')}
                        onChange={handleChangeOccupationStatus}
                        options={formLists.occupationStatusOptions}
                        allowClear
                      />
                    </Form.Item>
                  </>
                )}
                {!user?.isMentor && isSurveyForm && isBusinessFieldsAvailable && <Divider className="profile-user-form__divider">
                  <Form.Item
                    layout="vertical"
                    label={t('profile.business_start_period_label')}
                    field="business_start_period"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_business_start_period'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_business_start_period'))
                    }]}
                  >
                    <Select
                      placeholder={t('profile.business_start_period_placeholder')}
                      options={formLists.business_start_period}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    layout="vertical"
                    label={t('profile.mwbe_label')}
                    field="mwbe"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_mwbe'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_mwbe'))
                    }]}
                  >
                    <Select
                      placeholder={t('profile.mwbe_placeholder')}
                      options={formLists.mwbe}
                      allowClear
                      onChange={(value) => {
                        if (value !== 'Other') {
                          form.setFieldsValue({ mwbe_other: undefined });
                        }
                      }}
                    />
                  </Form.Item>
                  {showMwbeOther && (
                    <Form.Item
                      layout="vertical"
                      label={t('profile.mwbe_other_label')}
                      field="mwbe_other"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_mwbe_other'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_mwbe_other')) : cb()
                      }]}
                    >
                      <Input
                        placeholder={t('profile.mwbe_other_placeholder')}
                        autoComplete="off"
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    layout="vertical"
                    label={t('profile.employees_count_label')}
                    field="employees_count"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_employees_count'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_employees_count'))
                    }]}
                  >
                    <Select
                      placeholder={t('profile.employees_count_placeholder')}
                      options={formLists.employees_count}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    layout="vertical"
                    label={t('profile.business_stage_label')}
                    field="business_stage"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_business_stage'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_business_stage'))
                    }]}
                  >
                    <Select
                      placeholder={t('profile.business_stage_placeholder')}
                      options={formLists.business_stage}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    layout="vertical"
                    label={t('profile.business_location_label')}
                    field="business_location"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_business_location'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_business_location'))
                    }]}
                  >
                    <Select
                      placeholder={t('profile.business_location_placeholder')}
                      options={formLists.business_location}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    layout="vertical"
                    label={t('profile.revenue_label')}
                    field="revenue"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_revenue'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_revenue'))
                    }]}
                  >
                    <Select
                      placeholder={t('profile.revenue_placeholder')}
                      options={formLists.revenue}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    layout="vertical"
                    label={t('profile.items_type_label')}
                    field="item_types"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_items_type'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_items_type'))
                    }]}
                  >
                    <Select
                      placeholder={t('profile.items_type_placeholder')}
                      options={formLists.items_type}
                      allowClear
                    />
                  </Form.Item>
                </Divider>}

                {!user?.isMentor && isSurveyForm && (
                  <>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.first_goal_label')}
                      field="first_goal"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_first_goal'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_first_goal')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.first_goal_placeholder')}
                        onChange={(value) => setFirstGoal(value)}
                        options={getFirstGoalOptions()}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.second_goal_label')}
                      field="second_goal"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_second_goal'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_second_goal')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.second_goal_placeholder')}
                        onChange={(value) => setSecondGoal(value)}
                        options={getSecondGoalOptions()}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.third_goal_label')}
                      field="third_goal"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_third_goal'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_third_goal')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.third_goal_placeholder')}
                        onChange={(value) => setThirdGoal(value)}
                        options={getThirdGoalOptions()}
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.long_term_goal_label')}
                      field="long_term_goal"
                      rules={[{
                        required: isMenteeIntakeFieldRequired,
                        message: t('form_validation_messages.empty_long_term_goal'),
                        validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_long_term_goal')) : cb()
                      }]}
                    >
                      <Select
                        placeholder={t('profile.important_goal_placeholder')}
                        options={formLists.important_goal}
                        allowClear
                      />                    
                    </Form.Item>
                  </>
                )}
                {!user?.isMentor && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.professional_experience_label')}
                    field="professionalExperience"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_professional_experience'),
                      validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_professional_experience')) : cb()
                    }]}
                  >
                    <Select
                      placeholder={t('profile.professional_experience_placeholder')}
                      options={professionalExperienceOptions}
                      allowClear
                    />
                  </Form.Item>
                )}
                {!privateProfile.isMentor && useNycIntake && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.current_industry_label')}
                    field="current_industry"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_industries'),
                      validator: (v, cb) => {
                        if (!isMenteeIntakeFieldRequired) return cb();
                        if (!v) return cb(t('form_validation_messages.empty_industries'));
                        cb();
                      }
                    }]}
                  >
                    <Select
                      placeholder={t('profile.current_industry_placeholder')}
                      options={formLists.industriesNycOptions}
                      allowClear
                      showSearch
                    />
                  </Form.Item>
                )}

                {(privateProfile.isMentor || privateProfile.isAdmin) && (
                  <>
                    {/* advisor_title hidden from UI; value preserved for submit */}
                    <Form.Item field="advisor_title" style={{ display: 'none', margin: 0 }}>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.advisor_company_label')}
                      field="advisor_company"
                    >
                      <Input
                        placeholder={t('profile.advisor_company_placeholder')}
                        autoComplete="off"
                      />
                    </Form.Item>
                    <Form.Item
                      layout="vertical"
                      label={t('profile.sub_industry_label')}
                      field="sub_industry"
                    >
                      <Input
                        placeholder={t('profile.sub_industry_placeholder')}
                        autoComplete="off"
                      />
                    </Form.Item>
                    {useNycIntake && (
                      <Form.Item
                        layout="vertical"
                        label={t('profile.current_industry_label')}
                        field="current_industry"
                      >
                        <Select
                          placeholder={t('profile.current_industry_placeholder')}
                          options={formLists.currentIndustryOptions}
                          allowClear
                        />
                      </Form.Item>
                    )}
                    <Form.Item
                      layout="vertical"
                      label={t('profile.proficiency_label')}
                      field="categories"
                    >
                      <Select
                        placeholder={t('profile.proficiency_placeholder')}
                        mode="multiple"
                        maxTagCount={3}
                        value={selectedAdvisorCategories}
                        onChange={handleChangeAdvisorCategories}
                        options={getAdvisorCategoriesOptions(
                          useNycIntake ? formLists.advisorCategoriesOptions : formLists.categoriesOptions
                        )}
                        allowClear
                      />
                    </Form.Item>
                  </>
                )}
                {!useNycIntake && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.current_industry_label')}
                    field="current_industry"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_industries'),
                      validator: (v, cb) => {
                        if (!isMenteeIntakeFieldRequired || privateProfile.isMentor) return cb();
                        if (!v || v.length === 0) {
                          return cb(t('form_validation_messages.empty_industries'));
                        }
                        cb();
                      }
                    }]}
                  >
                    <Select
                      placeholder={t('profile.current_industry_placeholder')}
                      mode="multiple"
                      maxTagCount={3}
                      value={selectedIndustries}
                      onChange={handleChangeIndustries}
                      options={getIndustriesOptions(formLists.industriesNycOptions)}
                      allowClear
                    />
                  </Form.Item>
                )}
                {!isCalendlyLinkRequired && !isAutoGoogleMeetEnabled && privateProfile.isMentor && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.booking_link_label')}
                    field="bookingLink"
                    rules={[{
                      required: true,
                      message: t('form_validation_messages.empty_booking_link'),
                      validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_booking_link'))
                    }]}
                  >
                    <Input
                      placeholder={t('profile.booking_link_placeholder')}
                    />
                  </Form.Item>
                )}
                {isCalendlyLinkRequired && privateProfile.isMentor && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.calendly_link_label')}
                    field="calendlyLink"
                    rules={[
                      { required: calendlyFieldRequired, message: t('form_validation_messages.empty_calendly') }, {
                        validator: (value, callback) => validateUrlField(value, callback, t, calendlyFieldRequired, 'Calendly', 'calendly.com')
                      },]}
                  >
                    <Input
                      placeholder={t('profile.calendly_link_placeholder')}
                      onBlur={() => {
                        form.setFieldsValue({
                          calendlyLink: fixUrl(form.getFieldValue("calendlyLink")),
                        });
                      }}
                    />
                  </Form.Item>
                )}
                {!privateProfile.isMentor && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.high_industries_label')}
                    field="highGrowthIndustries"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_high_growth_industries'),
                      validator: (v, cb) => {
                        if (!isMenteeIntakeFieldRequired) return cb();
                        if (useNycIntake) {
                          if (!v) return cb(t('form_validation_messages.empty_high_growth_industries'));
                        } else if (!v || v.length === 0) {
                          return cb(t('form_validation_messages.empty_high_growth_industries'));
                        }
                        cb();
                      }
                    }]}
                  >
                    <Select
                      placeholder={t('profile.high_industries_placeholder')}
                      mode={useNycIntake ? undefined : "multiple"}
                      options={formLists.highGrowthIndustryOptions}
                      allowClear
                    />
                  </Form.Item>
                )}

                {!privateProfile.isMentor && isSurveyForm && (
                  <Form.Item
                    layout="vertical"
                    label={t('profile.source_label')}
                    field="howYouFoundUs"
                    rules={[{
                      required: isMenteeIntakeFieldRequired,
                      message: t('form_validation_messages.empty_how_you_found_us'),
                      validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_how_you_found_us')) : cb()
                    }]}
                  >
                    <Select
                      placeholder={t('profile.source_placeholder')}
                      options={formLists.howYouFoundUsOptions}
                      allowClear
                    />
                  </Form.Item>
                )}

                <FormItem
                  layout="vertical"
                  label={t('profile.website_label')}
                  field="website"
                  rules={[{ required: false }]}
                >
                  <Input
                    placeholder={t('profile.website_placeholder')}
                    value={privateProfile?.website}
                    onBlur={() => {
                      form.setFieldsValue({
                        website: fixUrl(form.getFieldValue("website")),
                      });
                    }}
                  />
                </FormItem>

                <Form.Item
                  layout="vertical"
                  label={
                    useNycIntake && !privateProfile.isMentor
                      ? t('profile.tagline_label')
                      : t('profile.short_description_label')
                  }
                  field="shortDescription"
                  rules={[{
                    required: isMenteeIntakeFieldRequired && !privateProfile.isMentor && !useNycIntake,
                    message: t('form_validation_messages.empty_short_description'),
                    validator: (v, cb) => (
                      isMenteeIntakeFieldRequired && !privateProfile.isMentor && !useNycIntake
                        ? checkEmptyField(v, cb, t('form_validation_messages.empty_short_description'))
                        : cb()
                    )
                  }]}
                >
                  <Input
                    placeholder={
                      useNycIntake && !privateProfile.isMentor
                        ? t('profile.tagline_placeholder')
                        : t('profile.short_description_placeholder')
                    }
                    maxLength={150}
                    showWordLimit
                  />
                </Form.Item>

                <FormItem
                  layout="vertical"
                  label={t('profile.bio_label')}
                  field="fullBio"
                  rules={[{
                    required: useNycIntake ? false : isMenteeIntakeFieldRequired,
                    message: t('form_validation_messages.empty_bio'),
                    validator: (v, cb) => (useNycIntake || !isMenteeIntakeFieldRequired) ? cb() : checkEmptyField(v, cb, t('form_validation_messages.empty_bio'))
                  }]}
                >
                  <TextArea
                    placeholder={t('profile.bio_placeholder')}
                    autoSize={{ minRows: 6, maxRows: 16 }}
                    maxLength={2600}
                    showWordLimit
                    style={{ whiteSpace: 'pre-line' }} 
                  />
                </FormItem>

                <Form.Item
                  layout="vertical"
                  label={t('profile.profile_picture_label')}
                  triggerPropName="fileList"
                  className="profile-user-form__profile-picture-item"
                >
                  {privateProfile && (
                    <div style={{ marginBottom: 16 }}>
                      <UserAvatar
                        avatarUrl={privateProfile.avatar_url}
                        firstName={privateProfile.name}
                        lastName={privateProfile.last_name}
                        size={96}
                        shape="circle"
                        alt={[privateProfile.name, privateProfile.last_name].filter(Boolean).join(' ')}
                      />
                    </div>
                  )}
                  <Upload
                    drag
                    withCredentials={true}
                    limit={1}
                    accept={{type: AVATAR_IMAGE_TYPES.join(), strict: false}}
                    action={endpoints.addAvatar}
                    headers={{ Authorization: `Bearer ${idToken}` }}
                    data={{ id: user?.id }}
                    tip={t('profile.profile_picture_info')}
                    className={classNames('avatar-upload', { 'avatar-upload__error': isFileTypeError })}
                    onChange={(fileList, file) => {
                      if (file.status === "done") {
                        setIsAvatarUpdating(false);
                        const response = file.response as { id: string };
                        const avatarURLObject: URL = new URL(response.id);
                        avatarURLObject.searchParams.set('hash', Date.now().toString());
                        const avatarURL: string = avatarURLObject.toString();
                        setPrivateProfile({
                          ...privateProfile,
                          avatar_url: avatarURL,
                        });
                        Message.success({ content: t('notification_messages.avatar_uploaded_success'), closable: true, duration: 3000 });
                        dispatch(setNewAvatar(avatarURL));
                      }
                    }}
                    onProgress={(e) => {
                      if (e.status === "uploading") {
                        setIsAvatarUpdating(true);
                      }
                    }}
                    beforeUpload={beforeUpload}
                  />
                </Form.Item>

                {/* SMS Settings Section - Inside the form */}
                <div style={{ marginTop: 30 }}>
                  <SMSSettings />
                </div>

                <FormItem layout="vertical">
                  <Button
                    htmlType="submit"
                    style={{
                      color: getButtonTextColor(),
                      backgroundColor: isAvatarUprating ? getButtonDisabledColor() : getButtonColor(),
                    }}
                    shape="round"
                    disabled={isAvatarUprating}
                  >
                    {t('profile.update_profile_button')}
                  </Button>
                </FormItem>
              </Form>
            </>
          )}
        </Col>
      </Row>
      {!!imageFile && (
        <AvatarCropDialog
          file={imageFile}
          visible={showCropDialog}
          onCancel={() => {
            setShowCropDialog(false);
            rejecterRef.current?.();
            clearRefs();
          }}
          onConfirm={(file) => {
            resolverRef.current?.(file);
            setShowCropDialog(false);
            clearRefs();
          }}
        />
      )}
    </Card>
  );
};